// @ts-nocheck
import * as Sentry from "@sentry/browser";
import createAuth0Client, {
  Auth0Client,
  GetIdTokenClaimsOptions,
  GetTokenSilentlyOptions,
  GetTokenWithPopupOptions,
  IdToken,
  LogoutOptions,
  PopupConfigOptions,
  PopupLoginOptions,
  RedirectLoginOptions,
} from "@auth0/auth0-spa-js";
import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useAnalytics } from "../lib/analytics/analytics";
import { stripParamsFromPath } from "../hooks/use_query_string";

import {
  hasPendingTransaction,
  hasRedirectCallbackParams,
  REDIRECT_CALLBACK_PARAMS,
} from "./auth0_callback";
import { getErrorMessage } from "./error_message";
import { identifyToSentry } from "./sentry";

const DEFAULT_REDIRECT_CALLBACK = () =>
  window.history.replaceState({}, document.title, window.location.pathname);

/**
 * Set when adopting a fresh sign-in fails, read once by the sign-in page. The
 * failure happens before Sentry is initialised and used to vanish entirely —
 * the person was silently returned to the sign-in page, which is
 * indistinguishable from never having signed in at all.
 */
export const LOGIN_ADOPTION_ERROR_KEY = "fm.login.adoptionError";

export const ADOPT_SESSION_TIMEOUT_SECONDS = 10;

/**
 * Adopts a session that exists at Auth0 but not yet in this client. Silent auth
 * presents the session cookie rather than a spent code, so it is the one
 * request that can still succeed once an exchange has failed.
 *
 * Deliberately asks again rather than inferring: a token can arrive without the
 * profile the signed-in flag is derived from.
 */
async function adoptSessionSilently(client: Auth0Client): Promise<boolean> {
  try {
    // Bounded on purpose. With no refresh token cached — exactly the case here,
    // the exchange having failed — the library falls back to silent auth in an
    // iframe and waits out its own 60s authorize timeout. Nobody is sent to
    // sign in until this settles, so an iframe that never answers (the Auth0
    // domain blocked or unreachable) would hold the dashboard on its loader for
    // a full minute. A session that is there answers in one round trip; longer
    // than this and there is nothing to adopt.
    await client.getTokenSilently({
      timeoutInSeconds: ADOPT_SESSION_TIMEOUT_SECONDS,
    });

    return await client.isAuthenticated();
  } catch {
    return false;
  }
}

/**
 * Adopts a sign-in just made on our own page, which returns here with the new
 * account's tokens in the address. The sign-in establishes the session with
 * Auth0 but none of the local state this client decides "signed in" from, so
 * the client would report nobody signed in and the app would send the person
 * straight back to the sign-in page. Asking for a token silently is what adopts
 * that session; the tokens in the address are never used.
 *
 * Not through adoptSessionSilently: a failure here needs the error itself,
 * which that helper answers without.
 */
async function adoptSignInFromOurPage(client: Auth0Client): Promise<boolean> {
  try {
    // The sign-in decides who is signed in, so whatever this browser still
    // holds from an earlier one goes first — an admin trying the invite they
    // just sent is still signed in as themselves. Left in place it wins: live
    // tokens are answered from the cache, and expired ones leave a refresh
    // token that mints more for the same account. Only this client's copy
    // goes; Auth0 holds the new session.
    await client.logout({ localOnly: true });
    // Bounded like adoptSessionSilently, and for the same reason: with nothing
    // cached, this is silent auth in an iframe.
    await client.getTokenSilently({
      timeoutInSeconds: ADOPT_SESSION_TIMEOUT_SECONDS,
    });

    const adopted = await client.isAuthenticated();

    window.history.replaceState(
      {},
      document.title,
      window.location.pathname + window.location.search
    );

    return adopted;
  } catch (error) {
    // captureException alone is not enough here: this runs before Sentry is
    // initialised, so the event is dropped. The sign-in page reads the stored
    // reason and both shows it and reports it.
    sessionStorage.setItem(
      LOGIN_ADOPTION_ERROR_KEY,
      (error as { error?: string; message?: string })?.error ??
        (error as { message?: string })?.message ??
        "unknown"
    );
    Sentry.captureException(error);

    return false;
  }
}

export interface PartnerLocation {
  locationId: string;
  locationName: string;
  locationStreetAddress: string;
  locationCity: string;
  locationTimeZone: string;
  live: boolean;
}

export enum PartnerType {
  WL = "wl",
  TEAMS = "teams",
  NON_PARTNER = "non_partner",
}

export interface UserProfile {
  partnerName: string;
  partnerId: string;
  partnerSlug: string;
  auth0UserId: string;
  partnerLocations: PartnerLocation[];
  partnerInternalName?: string;
  payoutInUSD: boolean;
  partnerType: PartnerType;
  /** From `GET /api/v2/partner_user` field `is_super_admin`. */
  isSuperAdmin?: boolean;
  /** Partner-scoped role. Fetched at login, so it can go stale mid-session. */
  role?: PartnerUserRole;
  /** The partner has its own Postmark server, so there is an unsubscribers list to download. */
  hasPostmarkServer?: boolean;
}

export type PartnerUserRole = "admin" | "standard";

interface Auth0Context {
  isAuthenticated: boolean;
  user: any;
  token: string;
  userProfile?: UserProfile;
  /**
   * The error from the last profile fetch, when it failed, and cleared the
   * moment one succeeds. The route gate needs more than `!userProfile` to
   * decide what to show — a person whose access was removed and a person whose
   * token was rejected both arrive with no profile, and only one of them is
   * actually unauthorized — and it reaches that verdict through
   * `isAccessRemoved`, so the classification stays in one place.
   */
  userProfileError?: unknown;
  loading: boolean;
  popupOpen: boolean;
  loginWithPopup: (
    options?: PopupLoginOptions,
    config?: PopupConfigOptions
  ) => Promise<void>;
  handleRedirectCallback: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  retryUserProfile: () => Promise<void>;
  getIdTokenClaims: (options?: GetIdTokenClaimsOptions) => Promise<IdToken>;
  loginWithRedirect: (options?: RedirectLoginOptions) => Promise<void>;
  getTokenSilently: (options?: GetTokenSilentlyOptions) => Promise<any>;
  getTokenWithPopup: (
    options?: GetTokenWithPopupOptions,
    config?: PopupConfigOptions
  ) => Promise<string>;
  logout: (options?: LogoutOptions) => Promise<void>;
  /**
   * Drops the local signed-in state once Auth0 has reported the session is
   * over, so that whoever is left holding a dead token is treated as signed
   * out rather than as a fault.
   *
   * Deliberately clears the flag the route gate reads instead of redirecting
   * from here: which sign-in someone gets is `PrivateRoute`'s decision — it
   * varies by feature flag — and it also records the deep link to return them
   * to afterwards. Nothing is signed out at Auth0, because there is no session
   * left there to end.
   */
  endSession: () => void;
  setToken: React.Dispatch<React.SetStateAction<string>>;
  setUser: React.Dispatch<React.SetStateAction<any>>;
}

const defaultAuth0Context: Auth0Context = {
  isAuthenticated: false,
  user: undefined,
  token: "",
  userProfile: undefined,
  loading: true,
  popupOpen: false,
  loginWithPopup: () => Promise.resolve(),
  handleRedirectCallback: () => Promise.resolve(),
  refreshUserProfile: () => Promise.resolve(),
  retryUserProfile: () => Promise.resolve(),
  getIdTokenClaims: () => Promise.resolve({ __raw: "" }),
  loginWithRedirect: () => Promise.resolve(),
  getTokenSilently: () => Promise.resolve(),
  getTokenWithPopup: () => Promise.resolve(""),
  logout: () => Promise.resolve(),
  endSession: () => {},
  setToken: () => {},
  setUser: () => {},
};

export const Auth0Context = React.createContext(defaultAuth0Context);
export const useAuth0 = () => useContext(Auth0Context);

interface Auth0ProviderProps {
  children: React.ReactNode;
  /**
   * Where to send someone once a sign-in comes back. Whatever it does, it must
   * also retire the callback address — those parameters are single-use, and
   * leaving them anywhere reachable, a history entry included, leaves a sign-in
   * that can be replayed but never completed.
   */
  onRedirectCallback: Function;
  domain: string;
  clientId: string;
  redirectUri?: string;
  audience: string;
}

/** Auth0 / API may expose boolean claims as actual booleans or the string `"true"` after JWT round-trips. */
function isAuth0BooleanTruthy(value: unknown): boolean {
  return value === true || value === "true";
}

function toUserProfile(data: any): UserProfile {
  return {
    partnerName: data.partner_name,
    partnerInternalName: data.partner_internal_name,
    partnerId: data.partner_id,
    partnerSlug: data.partner_slug,
    auth0UserId: data.auth0_user_id,
    partnerLocations: data.partner_locations
      ? data.partner_locations.map((pl: any) => ({
          locationId: pl.location_id,
          locationName: pl.location_name,
          locationStreetAddress: pl.location_street_address,
          locationCity: pl.location_city,
          locationTimeZone: pl.location_time_zone,
          live: pl.live,
        }))
      : [],
    payoutInUSD: data.payout_in_usd,
    partnerType: data.partner_type,
    isSuperAdmin: isAuth0BooleanTruthy(data.is_super_admin),
    role: data.role,
    hasPostmarkServer: data.has_postmark_server === true,
  };
}

/**
 * Carries everything that separates one refusal from another, because the
 * status cannot: `/api/v2/partner_user` answers 401 both for a person whose
 * dashboard access was removed and for a token it rejected
 * (`Secured#authenticate_request!`).
 */
export class PartnerUserRefusedError extends Error {
  readonly status: number;
  readonly reason?: string;
  readonly code?: string;

  constructor(status: number, reason?: string, code?: string) {
    // The reason rides in the message so the refusals are legible as separate
    // things in an issue list, where only the title shows. Grouping does not
    // depend on it — the fingerprint is explicit — so a reworded backend
    // message moves a title without splitting a group.
    super(
      reason
        ? `partner_user responded ${status}: ${reason}`
        : `partner_user responded ${status}`
    );
    this.name = "PartnerUserRefusedError";
    this.status = status;
    this.reason = reason;
    this.code = code;
  }
}

/**
 * Codes for the ways the profile endpoint refuses, read in preference to the
 * message.
 *
 * A code is the only thing that can express the distinction completely. The
 * removed-access message is raised by a `rescue` covering the whole
 * authentication method, so it also answers for a super admin whose selected
 * partner no longer exists — a different person with a different remedy,
 * wearing identical prose.
 */
export const PARTNER_USER_ERROR_CODES = {
  ACCESS_REMOVED: "partner_user_not_found",
  TOKEN_REJECTED: "token_invalid",
} as const;

/**
 * The message the backend sends for removed access while it sends no code.
 * Consulted only when a refusal carries no code at all, and deletable once
 * every refusal does.
 */
const ACCESS_REMOVED_MESSAGE = "Not found in partner users";

/**
 * Read what a refusal says about itself. Three body shapes reach here: the
 * authentication failures answer `{ errors: [...] }`, the rest of the API
 * answers `{ message, code }`, and the endpoint's own guard answers with no
 * body at all.
 */
async function readRefusal(
  response: Response
): Promise<{ reason?: string; code?: string }> {
  try {
    const body = await response.json();
    const reason = body?.errors?.[0] ?? body?.message ?? body?.error;
    const code = body?.code;

    return {
      reason: typeof reason === "string" ? reason : undefined,
      code: typeof code === "string" ? code : undefined,
    };
  } catch {
    return {};
  }
}

/**
 * Whether a failed profile fetch means the person genuinely has no access —
 * the one refusal the refusal screen tells the truth about.
 *
 * A code, once present, is authoritative; the message is consulted only in its
 * absence. Either way an unrecognised refusal is deliberately *not* read as
 * removed access: getting that wrong costs a removed person a retry that keeps
 * failing, while the opposite tells a partner admin who still has access that
 * they do not, on a screen they cannot get off. The asymmetry is the whole
 * reason this predicate exists, so it errs toward recoverable.
 */
export function isAccessRemoved(error: unknown): boolean {
  if (!(error instanceof PartnerUserRefusedError) || error.status !== 401) {
    return false;
  }

  return error.code
    ? error.code === PARTNER_USER_ERROR_CODES.ACCESS_REMOVED
    : error.reason === ACCESS_REMOVED_MESSAGE;
}

/**
 * A refusal of the token rather than of the person — so the same request with
 * a newly minted token is expected to succeed.
 */
export function isTokenRejected(error: unknown): boolean {
  return (
    error instanceof PartnerUserRefusedError &&
    error.status === 401 &&
    !isAccessRemoved(error)
  );
}

// Exported for its tests only.
//
// The failure case here is a person, not a network: a removed dashboard user
// still signs in at Auth0 and this request answers 401. Parsing that error
// body used to produce a profile of undefineds — truthy, but with no partner —
// which read as "super admin who has not picked a partner yet" and sent the
// person to the partner picker to crash. Refusing loudly is what routes them
// to the refusal screen instead.
export async function getUserProfile(authToken: string): Promise<UserProfile> {
  const response = await fetch("/api/v2/partner_user", {
    headers: {
      Authorization: `Bearer ${authToken}`,
    },
  });

  if (!response.ok) {
    const { reason, code } = await readRefusal(response);

    throw new PartnerUserRefusedError(response.status, reason, code);
  }

  return toUserProfile(await response.json());
}

/**
 * Which fault a failed profile fetch belongs to, for grouping.
 *
 * Deliberately coarser than the status: a gateway flapping across 502, 503 and
 * 504 is one incident, and grouping on the status would fan it into three
 * issues. The exact status stays on a tag, where it filters within the group
 * instead of splitting it.
 *
 * Removed access has no bucket because it is never reported — see
 * `reportProfileFailure`.
 */
function profileFailureBucket(error: unknown): string {
  if (isTokenRejected(error)) return "rejected-token";

  return "unavailable";
}

// Sentry truncates a tag value past 200 characters. The reason can be a raw
// backend exception message — a 500 answers `{ message: e.message }` — so it is
// cut here rather than server-side, where the overflow is reported as an error
// on the event instead of just a shorter tag.
const MAX_TAG_LENGTH = 200;

/**
 * Where a failed fetch was asked from, because the same fault costs wildly
 * different things depending on the answer: the first load is someone who
 * cannot get in at all, while the refresh after a location save is a background
 * top-up nobody saw fail. A tag rather than a fingerprint segment, so the two
 * stay one group and stay separable within it.
 */
type ProfileFetchOrigin = "initial-load" | "refresh" | "retry";

/**
 * Report a profile that never arrived, split by what it means.
 *
 * Removed access is not one of them. It is the endpoint answering correctly:
 * someone deleted in Team settings keeps a valid Auth0 session, signs in with
 * it, and the refusal screen tells them so. Nobody has ever had an action to
 * take on the report, and a warning is not a cheaper way of saying that — it
 * still lands in the issue stream to be read past, and every stale-session
 * sign-in files another one. So it is dropped here rather than downgraded.
 *
 * Dropped at the capture and not in `beforeSend`, because by then the
 * distinction is gone: a rejected token — a partner admin locked out of a
 * dashboard they should still have — is also a 401, and telling the two apart
 * again would mean re-deriving `isAccessRemoved` from the tags it wrote.
 *
 * What survives is what someone can act on, so it is all reported at `error`.
 * Grouped on the bucket alone, with no `{{ default }}` segment, so a group
 * survives both a release and a reworded message.
 *
 * `partner_user_status` is load-bearing beyond diagnosis: `processSentryEvent`
 * reads it to let this capture past the bare-fetch drop, which would otherwise
 * discard every fault where the request never reached the server.
 */
function reportProfileFailure(
  error: unknown,
  origin: ProfileFetchOrigin,
  recoveryFailure?: unknown
) {
  if (isAccessRemoved(error)) return;

  const refused = error instanceof PartnerUserRefusedError ? error : undefined;

  Sentry.captureException(error, {
    level: "error",
    fingerprint: [
      "fm-partner-user-profile-failed",
      profileFailureBucket(error),
    ],
    tags: {
      partner_user_status: refused?.status ?? "unreachable",
      partner_user_reason:
        refused?.reason?.slice(0, MAX_TAG_LENGTH) ?? "unknown",
      partner_user_code: refused?.code ?? "none",
      partner_user_fetch_origin: origin,
      // Set only when the silent recovery could not even mint a token, which
      // is how an expired Auth0 session reaches here. Without it that session
      // failure is the reported error and the 401 it was recovering from is
      // reported nowhere.
      partner_user_recovery_error: recoveryFailure
        ? getErrorMessage(recoveryFailure).slice(0, MAX_TAG_LENGTH) || "unknown"
        : "none",
    },
  });
}

export const Auth0Provider = ({
  children,
  onRedirectCallback = DEFAULT_REDIRECT_CALLBACK,
  domain,
  clientId,
  redirectUri,
  audience,
}: Auth0ProviderProps) => {
  const history = useHistory();
  const [isAuthenticated, setIsAuthenticated] = useState();
  const [user, setUser] = useState();
  const [userProfile, setUserProfile] = useState();
  const [userProfileError, setUserProfileError] = useState();
  const [token, setToken] = useState("");
  const [auth0Client, setAuth0] = useState();
  const [loading, setLoading] = useState(true);
  const [popupOpen, setPopupOpen] = useState(false);
  const analytics = useAnalytics();

  const trackLoginEvent = (
    id,
    email,
    name,
    partnerId,
    partnerName,
    partnerInternalName
  ) => {
    analytics.identify(id, {
      email: email,
      name: name,
    });

    analytics.screen(
      "User Login",
      {
        $email: email,
        partnerName: partnerName,
        partnerInternalName,
        partnerId: partnerId,
        pageName: "Login",
      },
      { onlyProviders: ["mixpanel"] }
    );
  };

  const storeUserProfile = async (authToken: string) => {
    const profile = await getUserProfile(authToken);

    setUserProfile(profile);
    setUserProfileError(undefined);

    return profile;
  };

  /**
   * A fresh token, bypassing the cached one. Bounded for the same reason the
   * session probe is: with no refresh token cached this falls back to silent
   * auth in an iframe and waits out the library's own 60s authorize timeout,
   * which a person watching a spinner would read as hung.
   */
  const mintFreshToken = (client: Auth0Client) =>
    client.getTokenSilently({
      ignoreCache: true,
      timeoutInSeconds: ADOPT_SESSION_TIMEOUT_SECONDS,
    });

  const recordProfileFailure = (
    error: unknown,
    origin: ProfileFetchOrigin,
    recoveryFailure?: unknown
  ) => {
    setUserProfileError(error);
    reportProfileFailure(error, origin, recoveryFailure);
  };

  /**
   * The single way a profile is fetched, so every path records the same thing
   * on the way out: the profile on success, and on failure the error itself,
   * for the route gate to classify. Answers undefined rather than throwing —
   * no caller can do anything with the error but stop, and each of them sits
   * on a path that must still reach its own `setLoading(false)`.
   *
   * A rejected token is recovered from silently first. It is the most common
   * recoverable cause, and every other dashboard request already heals it
   * invisibly by minting a fresh token and replaying, so surfacing it here
   * would charge a person a screen and a click for what is repaired everywhere
   * else without one. Exactly one attempt: a token refused twice will not be
   * fixed by asking a third time.
   *
   * The client is passed rather than read from state because the first call
   * happens during initialisation, before the state holding it has been set.
   */
  const loadUserProfile = async (
    authToken: string,
    client: Auth0Client,
    origin: ProfileFetchOrigin = "initial-load"
  ) => {
    try {
      return await storeUserProfile(authToken);
    } catch (error) {
      if (!isTokenRejected(error)) {
        recordProfileFailure(error, origin);

        return undefined;
      }

      try {
        const freshToken = await mintFreshToken(client);

        setToken(freshToken);

        return await storeUserProfile(freshToken);
      } catch (retryError) {
        // Which of the two errors describes the fault depends on how the
        // recovery died. A second refusal is the better answer — it is the
        // backend's verdict on a brand-new token. A failure to mint one at all
        // is not: reporting it would file an expired Auth0 session under
        // "unavailable" with no status, losing the 401 that is the whole reason
        // this split exists. So the refusal is reported and the recovery
        // failure rides along on a tag.
        const refused = retryError instanceof PartnerUserRefusedError;

        recordProfileFailure(
          refused ? retryError : error,
          origin,
          refused ? undefined : retryError
        );

        return undefined;
      }
    }
  };

  useEffect(() => {
    const initAuth0 = async () => {
      const clientOptions = {
        domain,
        client_id: clientId,
        redirect_uri: redirectUri,
        audience: audience,
        cacheLocation: "localstorage",
        useRefreshTokens: true,
      };

      // `createAuth0Client` is the constructor plus a session probe, and only
      // the probe rejects: a network fault on it throws rather than reporting
      // nobody signed in. That must not cost us the client itself — every
      // method on the context below dereferences one, so ending the loading
      // state with none would make the route gate's own call to sign in throw
      // instead of redirecting, stranding a blank page with no way off it. The
      // unprobed client is the same client; it just has not asked yet.
      let auth0FromHook: Auth0Client;

      try {
        auth0FromHook = await createAuth0Client(clientOptions);
      } catch (error) {
        Sentry.captureException(error, {
          fingerprint: ["auth0-session-probe-failed"],
        });

        auth0FromHook = new Auth0Client(clientOptions);
      }

      setAuth0(auth0FromHook);

      if (hasRedirectCallbackParams(window.location.search)) {
        const transactionPresent = hasPendingTransaction(clientId);

        try {
          const { appState } = await auth0FromHook.handleRedirectCallback();
          onRedirectCallback(history, appState);
        } catch (error) {
          // The address is cleared first: while the spent parameters are
          // still in it, a reload only arrives back here.
          history.replace(
            stripParamsFromPath(
              window.location.pathname + window.location.search,
              REDIRECT_CALLBACK_PARAMS
            )
          );

          // Whoever this is authenticated at Auth0 moments ago, so the
          // session is usually still there to be adopted — and adopting it
          // makes a failed exchange an ordinary signed-in load.
          const adopted = await adoptSessionSilently(auth0FromHook);

          // Own group, split by outcome: an adopted session is noise nobody
          // saw, while an unrecovered one put a person back on the sign-in
          // page. Tagged as well as fingerprinted, because a fingerprint
          // segment cannot be queried.
          Sentry.captureException(error, {
            level: adopted ? "warning" : "error",
            fingerprint: [
              "auth0-redirect-callback-failed",
              adopted ? "adopted" : "unrecovered",
            ],
            tags: {
              auth0_transaction_present: transactionPresent,
              auth0_session_adopted: adopted,
            },
          });
        }
      }

      const isAuthenticated = window.location.hash.includes("access_token=")
        ? await adoptSignInFromOurPage(auth0FromHook)
        : await auth0FromHook.isAuthenticated();

      setIsAuthenticated(isAuthenticated);

      if (isAuthenticated) {
        // A token that fails to arrive is not a person without access, and now
        // that the loading state always ends, holding the signed-in flag
        // through one would show them the refusal screen — the route gate reads
        // "a user but no profile" as unauthorized. Clearing the flag sends them
        // back through sign-in instead, which an intact Auth0 session completes
        // without prompting for anything.
        let user;
        let token;

        try {
          user = await auth0FromHook.getUser();
          token = await auth0FromHook.getTokenSilently();
        } catch (error) {
          Sentry.captureException(error, {
            fingerprint: ["auth0-post-signin-token-failed"],
          });
          setIsAuthenticated(false);

          return;
        }

        setUser(user);
        setToken(token);
        identifyToSentry(user);

        const userProfile = await loadUserProfile(token, auth0FromHook);

        if (userProfile) {
          trackLoginEvent(
            user?.sub,
            user?.email,
            user?.nickname,
            userProfile?.partnerId,
            userProfile.partnerName,
            userProfile.partnerInternalName
          );
        }
      }
    };

    // Nothing in here may leave the dashboard loading: the route gate renders
    // the loader until it clears and will not send anyone to sign in before
    // then, so a failure that skips this strands the page on a spinner. The
    // rejection still surfaces — this only guarantees the state ends.
    void initAuth0().finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const loginWithPopup = async (params = {}) => {
    setPopupOpen(true);

    try {
      await auth0Client.loginWithPopup(params);
    } catch (error) {
      console.error(error);
    } finally {
      setPopupOpen(false);
    }

    const user = await auth0Client.getUser();
    setUser(user);
    setIsAuthenticated(true);
    identifyToSentry(user);

    const token = await auth0Client.getTokenSilently();
    setToken(token);

    // A missing profile is a state, not a stop: the person signed in but has no
    // partner access, and the routing shows them the refusal screen. Answering
    // undefined rather than throwing is also what keeps setLoading reachable —
    // a throw here would hold the spinner forever.
    const userProfile = await loadUserProfile(token, auth0Client);

    if (userProfile) {
      trackLoginEvent(
        user?.email,
        user?.nickname,
        userProfile?.partnerId,
        userProfile.partnerName,
        userProfile.partnerInternalName
      );
    }

    setLoading(false);
  };

  const handleRedirectCallback = async () => {
    setLoading(true);

    await auth0Client.handleRedirectCallback();

    const user = await auth0Client.getUser();
    setUser(user);

    setIsAuthenticated(true);
    identifyToSentry(user);

    const token = await auth0Client.getTokenSilently();
    setToken(token);

    // Same shape as above: a refused profile must still end the loading state,
    // or a removed user signing in through the hosted page waits forever.
    const userProfile = await loadUserProfile(token, auth0Client);

    if (userProfile) {
      trackLoginEvent(
        user?.email,
        user?.nickname,
        userProfile?.partnerId,
        userProfile.partnerName,
        userProfile.partnerInternalName
      );
    }

    setLoading(false);
  };

  const refreshUserProfile = async () => {
    if (!token) return;

    await loadUserProfile(token, auth0Client, "refresh");
  };

  /**
   * The retry behind the "could not confirm your access" screen, for the
   * faults the silent recovery cannot fix — a server failure, a network fault,
   * or a token still refused on the second ask. It mints a fresh token again
   * because a session can come back between two clicks.
   *
   * The fetch is made directly rather than through `loadUserProfile`, whose own
   * recovery would mint a *second* fresh token off the one just minted here:
   * two authorize round trips, each waiting out its own timeout, before the
   * button a person is watching comes back. One click is one attempt.
   */
  const retryUserProfile = async () => {
    let freshToken;

    try {
      freshToken = await mintFreshToken(auth0Client);
    } catch (error) {
      setUserProfileError(error);
      Sentry.captureException(error, {
        fingerprint: ["fm-profile-retry-token-failed"],
      });

      return;
    }

    setToken(freshToken);

    try {
      await storeUserProfile(freshToken);
    } catch (error) {
      recordProfileFailure(error, "retry");
    }
  };

  return (
    <Auth0Context.Provider
      value={{
        isAuthenticated,
        user,
        token,
        userProfile,
        userProfileError,
        loading,
        popupOpen,
        loginWithPopup,
        handleRedirectCallback,
        refreshUserProfile,
        retryUserProfile,
        getIdTokenClaims: (options?: GetIdTokenClaimsOptions) =>
          auth0Client.getIdTokenClaims(options),
        loginWithRedirect: (options?: RedirectLoginOptions) =>
          auth0Client.loginWithRedirect(options),
        getTokenSilently: (options?: GetTokenSilentlyOptions) =>
          auth0Client.getTokenSilently(options),
        getTokenWithPopup: (
          options?: GetTokenWithPopupOptions,
          config?: PopupConfigOptions
        ) => auth0Client.getTokenWithPopup(options, config),
        logout: (options?: LogoutOptions) => auth0Client.logout(options),
        endSession: () => setIsAuthenticated(false),
        setToken,
        setUser,
      }}
    >
      {children}
    </Auth0Context.Provider>
  );
};
